import { useEffect, useState } from "react";
import { useGlobalReducer } from "../../../GlobalContext";
import { GetDataImportStatus } from "../../api/dataImportService";

/** The warehouse's `DataImportStatus` for one import. */
export interface ImportStatus {
  Name?: string;
  Source?: string;
  Stage: number;
  Message?: string;
  ListId?: string;
  NewListId?: string;
  ListName?: string;
  ListMemberCount?: number;
  ProcessedCount?: number;
  ErrorCount?: number;
  NewContactCount?: number;
  NewDuplicateCount?: number;
  ExistingContactCount?: number;
  ExistingDuplicateCount?: number;
  Errors?: Record<string, string>;
}

// The warehouse's DataImportStage values this page acts on.
export const DoneStage = 1;
// CreatingProfiles, CreatingCRMContacts and UpdatingCRMContacts work through the file
// in batches and can run for minutes.
const LongStages = [0, 7, 9];

const ShortInterval = 2000;
const LongInterval = 10000;
// CreatingProfiles is also the stage the CSV is parsed in, and a bad file fails within
// moments, so the first checks stay quick whatever the stage.
const QuickStartPeriod = 10000;
const MaxWatchPeriod = 30 * 60 * 1000;

/**
 * Reads an import's status until it's done: often on a quick stage, every ten seconds on
 * a batched one, and not at all after half an hour. Undefined until the first read, null
 * when the warehouse has no status for the import.
 */
export function useImportStatus(importId: string) {
  const globalDispatch = useGlobalReducer();
  const [status, setStatus] = useState<ImportStatus | null | undefined>();
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stage: number | undefined;
    const startedAt = Date.now();

    const read = async () => {
      const response = await GetDataImportStatus(globalDispatch, importId);

      if (cancelled) {
        return;
      }

      if (response?.status === 400) {
        setStatus(null);
        return;
      }

      // Any other failure has been notified already; the next read may succeed.
      if (response?.data) {
        stage = (response.data as ImportStatus).Stage;
        setStatus(response.data as ImportStatus);
      }

      if (stage === DoneStage) {
        return;
      }

      const elapsed = Date.now() - startedAt;

      if (elapsed >= MaxWatchPeriod) {
        setPaused(true);
        return;
      }

      const isSlow =
        elapsed >= QuickStartPeriod &&
        stage !== undefined &&
        LongStages.includes(stage);
      timer = setTimeout(read, isSlow ? LongInterval : ShortInterval);
    };

    read();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [globalDispatch, importId]);

  return { status, paused };
}
